/**
 * The pointer capture behind an armed drawing tool.
 *
 * This is the part of drawing that genuinely needs a mouse, and it is
 * deliberately the *only* part: it converts pixels into data values and hands
 * them to `plan/annotation-draw.ts`, which decides everything else. A modifier
 * that also built annotations would put the shape rules somewhere no test can
 * reach — SciChart cannot be imported under jsdom, so anything living here is
 * verified by eye or not at all.
 *
 * ## Why a modifier rather than a DOM listener
 *
 * The surface is a WebGL canvas with its own hit-testing, zoom and pan
 * modifiers, and a `mousedown` listener on the element would fight all three:
 * it would fire underneath a dragged annotation's grip, it would not know the
 * axis calculators had been rebuilt mid-gesture, and it would keep firing after
 * a pan started. A modifier is inside that ordering, and returning early when
 * nothing is armed leaves the normal modifiers exactly as they were.
 *
 * ## The gesture is captured, then converted, then decided
 *
 * The *deciding* is `plan/draw-gesture.ts` and deliberately not here: this file
 * converts pixels and nothing else, because SciChart cannot be imported under
 * jsdom and a two-phase state machine with a click threshold has to be testable.
 * Press-drag-release and click-move-click both come out of that machine; all this
 * knows is how far the pointer travelled between the press and the release.
 *
 * `mousePoint` is in the parent canvas' space and the axes measure from the
 * series rect, so every point subtracts `seriesViewRect` before conversion.
 * With sub-surfaces that offset is how the volume pane knows it is not the price
 * pane; without it every drawing lands in the wrong pane by exactly the height
 * of the panes above it.
 */

import {
  ChartModifierBase2D,
  translateFromCanvasToSeriesViewRect,
  type ModifierMouseArgs,
  type SciChartSubSurface,
} from "scichart";

import type { DrawPoint } from "../plan/annotation-draw.js";
import { IDLE, advanceGesture, isClick, type DrawGesture } from "../plan/draw-gesture.js";
import type { AnnotationKind } from "../plan/annotation.js";

/** What the surface tells its host about a gesture. */
/** What a tool produces — a contract kind, or the trade-plan gesture. */
export type DrawKind = AnnotationKind | "plan";

export interface DrawingCallbacks {
  /** The gesture moved. Redraws the rubber band; never persists. */
  onPreview(kind: DrawKind, start: DrawPoint, current: DrawPoint, path: DrawPoint[]): void;
  /** The gesture finished. The host decides whether to keep it. */
  onCommit(kind: DrawKind, start: DrawPoint, end: DrawPoint, path: DrawPoint[]): void;
  /**
   * A first point is now placed and waiting for its second click, or is not.
   *
   * The only gesture state a host has any business knowing, and it needs this
   * one: with a point down, Escape should take the point back and leave the tool
   * armed, and without one it should disarm. Nothing else can tell the two apart.
   */
  onPlacingChange(placing: boolean): void;
}

/**
 * Captures a drag while a tool is armed, and is inert when one is not.
 *
 * Always attached rather than added and removed with the tool — a modifier
 * removed mid-gesture leaves the surface holding a pointer capture nobody owns,
 * which is the same reason `setInteractions` flips `isEnabled` instead of
 * detaching the axis-drag modifiers.
 */
export class DrawingModifier extends ChartModifierBase2D {
  readonly type = "QuFolioDrawing";

  /**
   * The armed tool, or `null`. Set by the builder when the palette changes.
   *
   * `"plan"` is not a contract kind: it is the trade-plan gesture, which
   * captures the same drag and produces a `Plan` rather than an `Annotation`.
   * It rides this modifier because the gesture is identical — press, drag,
   * release — and a second modifier for one more drag would be a second thing
   * to keep armed and disarmed in step.
   */
  tool: DrawKind | null = null;

  private gesture: DrawGesture = IDLE;

  /**
   * Where the press landed, in **pixels**.
   *
   * The machine decides in data space, but "was that a click" is a question
   * about the screen: a click is a few pixels of slop wherever the chart is
   * zoomed to, while the same slop in data values is a different distance on
   * every timeframe and every price. So the measurement stays here, with the
   * only code that has pixels.
   */
  private downPixel: { x: number; y: number } | null = null;

  constructor(private readonly callbacks: DrawingCallbacks) {
    super();
  }

  /**
   * Pixels to data values, in the coordinate space the axes actually use.
   *
   * Returns `null` when the calculators are not ready — during the first frame,
   * and after a resize until the next render pass. A gesture that starts then
   * would convert against a stale rect and land somewhere the user did not
   * click, so it is dropped rather than guessed at.
   */
  private toData(args: ModifierMouseArgs, outside = false): DrawPoint | null {
    const surface = this.parentSurface as SciChartSubSurface | undefined;
    const point = args.mousePoint;
    if (!surface || !point) return null;

    const xAxis = surface.xAxes.get(0);
    const yAxis = surface.yAxes.get(0);
    if (!xAxis || !yAxis) return null;

    // SciChart's own translation rather than subtracting the rect by hand —
    // `CursorModifier` uses exactly this, and the crosshair is already correct
    // across our sub-surfaces, so borrowing it means the drawing lands where
    // the crosshair says it will rather than agreeing with it by coincidence.
    const local = translateFromCanvasToSeriesViewRect(point, surface.seriesViewRect, outside);
    if (!local) return null;

    const xCalc = xAxis.getCurrentCoordinateCalculator();
    const yCalc = yAxis.getCurrentCoordinateCalculator();
    if (!xCalc || !yCalc) return null;

    return { x: xCalc.getDataValue(local.x), y: yCalc.getDataValue(local.y) };
  }

  /**
   * Hand one converted event to the machine and carry out what it says.
   *
   * **The state is advanced before the callback runs, never after.** The host
   * disarms the tool from inside `onCommit`, and a re-entrant mouse event
   * arriving against a half-finished gesture would commit the same drawing twice.
   */
  private step(tool: DrawKind, event: Parameters<typeof advanceGesture>[1]): void {
    const wasPlacing = this.gesture.phase === "place";
    const { state, effect } = advanceGesture(this.gesture, event, {
      freehand: tool === "freehand",
    });
    this.gesture = state;
    // Announced on the change only. It fires on every mouse move otherwise, and
    // a host that re-rendered on each would re-render on each.
    const placing = state.phase === "place";
    if (placing !== wasPlacing) this.callbacks.onPlacingChange(placing);
    if (effect.do === "preview") {
      this.callbacks.onPreview(tool, effect.start, effect.current, effect.path);
    } else if (effect.do === "commit") {
      this.downPixel = null;
      this.callbacks.onCommit(tool, effect.start, effect.end, effect.path);
    }
  }

  override modifierMouseDown(args: ModifierMouseArgs): void {
    const tool = this.tool;
    if (tool === null) {
      super.modifierMouseDown(args);
      return;
    }
    // Inside the plot area only. A press on an axis is a rescale drag, and a
    // tool armed must not turn one into a stray drawing pinned to the edge.
    const at = this.toData(args);
    if (!at) return;
    if (args.mousePoint) this.downPixel = { x: args.mousePoint.x, y: args.mousePoint.y };
    this.step(tool, { type: "down", at });
    // Not calling super: with a tool armed the press belongs to the drawing, and
    // letting the pan modifier see the same one would scroll the chart out from
    // under the line being drawn.
  }

  override modifierMouseMove(args: ModifierMouseArgs): void {
    const tool = this.tool;
    if (tool === null || this.gesture.phase === "idle") {
      super.modifierMouseMove(args);
      return;
    }
    // Out of bounds allowed from here: a gesture naturally leaves the plot area
    // on its way to a corner, and refusing those points would freeze the rubber
    // band at the edge and commit a shape the user did not draw.
    const at = this.toData(args, true);
    if (!at) return;
    this.step(tool, { type: "move", at });
  }

  override modifierMouseUp(args: ModifierMouseArgs): void {
    const tool = this.tool;
    if (tool === null || this.gesture.phase === "idle") {
      super.modifierMouseUp(args);
      return;
    }
    const at = this.toData(args, true);
    // **A release with nowhere to put it still has to reach the machine**, or a
    // drag that ended off the calculators would stay in `drag` for ever and the
    // next press anywhere would finish it. The press point is the honest
    // fallback: it is where the gesture was, and a zero-length shape is
    // discarded downstream by `isWorthKeeping`.
    const end = at ?? this.gesture.start;
    const pixel = args.mousePoint;
    // No pixels for either end means we cannot tell a click from a drag; treat it
    // as a drag, which commits, rather than leaving a point placed invisibly.
    const click =
      this.downPixel !== null && pixel !== undefined
        ? isClick(this.downPixel, { x: pixel.x, y: pixel.y })
        : false;
    this.step(tool, { type: "up", at: end, click });
  }

  /**
   * Abandon a gesture in progress.
   *
   * Called when the tool is disarmed mid-drag — the user pressed Escape, or
   * clicked the armed button again. Without it the next `mouseUp` anywhere on
   * the chart would commit a drawing whose start point belongs to a tool that
   * is no longer selected.
   */
  cancel(): void {
    const wasPlacing = this.gesture.phase === "place";
    this.gesture = IDLE;
    this.downPixel = null;
    // Cancelling is the other way out of `place`, and a host told the point went
    // down has to be told it came back up or Escape stops disarming for ever.
    if (wasPlacing) this.callbacks.onPlacingChange(false);
  }

  /**
   * Whether a first point is placed and waiting for its second click.
   *
   * The host needs it to know that Escape has something to abandon: with a tool
   * armed and a point down, Escape should take the point back and leave the tool
   * armed, rather than disarming a tool the user is still using.
   */
  get isPlacing(): boolean {
    return this.gesture.phase === "place";
  }

  toJSON(): never {
    // Not serialisable, and deliberately so: what is armed is view state (D8),
    // not workspace data. A saved workspace that restored an armed pencil would
    // have the next click on the chart draw something.
    return undefined as never;
  }
}
